import { router, usePage } from "@inertiajs/react"
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  DisclaimerOverlay,
  type DisclaimerOverlayMode,
} from "@/components/disclaimer-overlay"
import { settingsDisclaimerPath } from "@/routes"

interface DisclaimerUser {
  id?: number
  disclaimer_accepted_at?: string | null
}

interface DisclaimerPageProps {
  [key: string]: unknown
  auth?: {
    user?: DisclaimerUser | null
    shared_access?: boolean
  }
}

interface DisclaimerContextValue {
  openReadOnlyDisclaimer: () => void
}

const noop = () => {}

const DisclaimerContext = createContext<DisclaimerContextValue>({
  openReadOnlyDisclaimer: noop,
})

export function DisclaimerProvider({ children }: PropsWithChildren) {
  const acknowledgedVisibleDurationMs = 2000
  const acknowledgedExitDurationMs = 520
  const page = usePage<DisclaimerPageProps>()
  const { auth } = page.props
  const { url } = page
  const user = auth?.user ?? null
  const isSharedAccess = Boolean(auth?.shared_access)
  const [isReadOnlyOpen, setIsReadOnlyOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [acceptedLocally, setAcceptedLocally] = useState(false)
  const [dismissedForPage, setDismissedForPage] = useState(false)
  const [showAcknowledgedState, setShowAcknowledgedState] = useState(false)
  const [isClosingAcknowledged, setIsClosingAcknowledged] = useState(false)

  useEffect(() => {
    setIsReadOnlyOpen(false)
    setIsSubmitting(false)
    setAcceptedLocally(false)
    setDismissedForPage(false)
    setShowAcknowledgedState(false)
    setIsClosingAcknowledged(false)
  }, [user?.id])

  useEffect(() => {
    setIsReadOnlyOpen(false)
    setDismissedForPage(false)
  }, [url])

  useEffect(() => {
    if (!showAcknowledgedState || isClosingAcknowledged) return

    const timeoutId = window.setTimeout(() => {
      setIsClosingAcknowledged(true)
    }, acknowledgedVisibleDurationMs)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [
    showAcknowledgedState,
    isClosingAcknowledged,
    acknowledgedVisibleDurationMs,
  ])

  useEffect(() => {
    if (!showAcknowledgedState || !isClosingAcknowledged) return

    const timeoutId = window.setTimeout(() => {
      setShowAcknowledgedState(false)
      setIsClosingAcknowledged(false)
      setIsReadOnlyOpen(false)
    }, acknowledgedExitDurationMs)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [showAcknowledgedState, isClosingAcknowledged, acknowledgedExitDurationMs])

  const shouldShowRequiredPrompt = Boolean(
    user &&
      !isSharedAccess &&
      user.disclaimer_accepted_at === null &&
      !acceptedLocally &&
      !dismissedForPage,
  )
  const shouldKeepRequiredLayout =
    shouldShowRequiredPrompt || showAcknowledgedState
  const isOpen =
    shouldShowRequiredPrompt || isReadOnlyOpen || showAcknowledgedState
  const mode: DisclaimerOverlayMode = shouldKeepRequiredLayout
    ? "required"
    : "read_only"

  const openReadOnlyDisclaimer = useCallback(() => {
    setIsReadOnlyOpen(true)
  }, [])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (showAcknowledgedState) return

      if (open) {
        if (!shouldShowRequiredPrompt) {
          setIsReadOnlyOpen(true)
        }
        return
      }

      if (shouldShowRequiredPrompt) {
        setDismissedForPage(true)
        return
      }

      setIsReadOnlyOpen(false)
    },
    [shouldShowRequiredPrompt, showAcknowledgedState],
  )

  const handleContinue = useCallback(() => {
    if (!shouldShowRequiredPrompt || isSubmitting) return

    setIsSubmitting(true)
    router.patch(
      settingsDisclaimerPath(),
      {},
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          setShowAcknowledgedState(true)
          setIsClosingAcknowledged(false)
          setAcceptedLocally(true)
          setDismissedForPage(false)
        },
        onFinish: () => {
          setIsSubmitting(false)
        },
      },
    )
  }, [shouldShowRequiredPrompt, isSubmitting])

  const contextValue = useMemo(
    () => ({ openReadOnlyDisclaimer }),
    [openReadOnlyDisclaimer],
  )

  return (
    <DisclaimerContext.Provider value={contextValue}>
      {children}
      {user ? (
        <DisclaimerOverlay
          open={isOpen}
          mode={mode}
          isSubmitting={isSubmitting}
          showAcknowledgedState={showAcknowledgedState}
          isClosingAcknowledged={isClosingAcknowledged}
          onOpenChange={handleOpenChange}
          onContinue={handleContinue}
        />
      ) : null}
    </DisclaimerContext.Provider>
  )
}

export function useDisclaimer() {
  return useContext(DisclaimerContext)
}
